<template>
    <section>
        <h2 class="scaffold-body-small-medium text-valence-stone-900 mb-2.5">Category Scores</h2>
        <BaseCard theme="cream" padding="lg">
            <ScoreLegend v-if="showSelfComparison" :subject-name="subjectName" class="mb-5" />
            <div class="flex flex-col gap-5">
                <div v-for="group in sectionGroups" :key="group.parent.section_id">
                    <!-- Parent heading (only when it has children) -->
                    <div v-if="group.children.length > 0" class="mb-4">
                        <ScoreRow
                            :label="group.parent.title ?? ''"
                            :avg="group.parent.avg"
                            :self-avg="group.parent.self_avg"
                            :max-score="maxScore"
                            :show-self-comparison="showSelfComparison" />
                        <div class="flex flex-col gap-3 pl-3 border-l-2 border-valence-cream-200 mt-3">
                            <ScoreRow
                                v-for="child in group.children"
                                :key="child.section_id"
                                :label="child.title ?? ''"
                                :avg="child.avg"
                                :self-avg="child.self_avg"
                                :max-score="maxScore"
                                :show-self-comparison="showSelfComparison" />
                        </div>
                    </div>

                    <!-- Standalone section (no children) -->
                    <ScoreRow
                        v-else
                        :label="group.parent.title ?? ''"
                        :avg="group.parent.avg"
                        :self-avg="group.parent.self_avg"
                        :max-score="maxScore"
                        :show-self-comparison="showSelfComparison" />
                </div>
            </div>
        </BaseCard>
    </section>
</template>

<script setup lang="ts">
import ScoreLegend from "~vue/AssessmentResults/ScoreLegend.vue";
import ScoreRow from "~vue/AssessmentResults/ScoreRow.vue";
import BaseCard from "~vue/components/BaseCard.vue";
import { computed } from "vue";

import type { SectionAggregate } from "./types";

interface Props {
    sections: SectionAggregate[];
    selfAssessEnabled: boolean;
    maxScore: number;
    // LAB-795: a self-assessment-only cycle has no raters, so the self-vs-others
    // legend and overlay must not render (the primary bar is the participant's
    // own rating, not an "others' avg").
    isSelfAssessmentOnly?: boolean;
    // Set when someone other than the subject (a team lead) is viewing;
    // switches the legend to third person.
    subjectName?: string;
}

const props = withDefaults(defineProps<Props>(), {
    maxScore: 5,
    isSelfAssessmentOnly: false,
});

const showSelfComparison = computed(() => props.selfAssessEnabled && !props.isSelfAssessmentOnly);

interface SectionGroup {
    parent: SectionAggregate;
    children: SectionAggregate[];
}

const sectionGroups = computed<SectionGroup[]>(() => {
    const topLevel = props.sections.filter((s) => s.depth === 0 && s.avg !== null).sort((a, b) => a.order - b.order);
    return topLevel.map((parent) => ({
        parent,
        children: props.sections.filter((s) => s.parent_section_id === parent.section_id && s.avg !== null).sort((a, b) => a.order - b.order),
    }));
});
</script>
